import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { customersApi, type CustomerSearchResult } from '../../api/customers.api.js';
import {
  inboundPaymentsApi,
  type InboundPayment,
  type InboundPaymentDetail,
  type InboundQueueFilter,
} from '../../api/inbound-payments.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { IncomingCommissionPanel } from '../../components/IncomingCommissionPanel.js';
import { CommentsPanel } from '../../components/CommentsPanel.js';
import { ReviewDocumentsPanel } from '../../components/ReviewDocumentsPanel.js';
import { PaymentScreeningPanel } from '../../components/PaymentScreeningPanel.js';
import { RecordManualPaymentDialog } from './RecordManualPaymentDialog.js';
import { providerTransactionEvidence } from '../../utils/provider-transaction-evidence.js';

/**
 * The compliance officer's queue for money that has arrived.
 *
 * Nothing on this screen has reached a customer's balance. A payment sits here
 * until someone releases it, and the customer sees it as pending in the
 * meantime — visible, so they know they were paid, and unspendable, so a
 * payment that turns out to be wrong is refused rather than clawed back.
 *
 * Three numbers are always shown together — what arrived, what we charge, and
 * what the customer receives — because the officer is approving all three, and
 * a fee nobody looked at is a fee nobody can defend.
 */

const FILTERS: readonly { value: InboundQueueFilter; label: string }[] = [
  { value: 'AWAITING', label: 'Awaiting a decision' },
  { value: 'PENDING_REVIEW', label: 'Attributed, awaiting release' },
  { value: 'UNATTRIBUTED', label: 'Nobody to credit' },
  { value: 'APPROVED', label: 'Released' },
  { value: 'REJECTED', label: 'Refused' },
];

type ActionKind = 'approve' | 'reject' | 'attribute' | 'reverse';

function queueFilterFromUrl(value: string | null): InboundQueueFilter {
  return FILTERS.some((filter) => filter.value === value)
    ? (value as InboundQueueFilter)
    : 'AWAITING';
}

import { formatTransactionAmount as money } from '../../utils/transaction-money.js';
import { SenderScreeningPanel } from '../../components/SenderScreeningPanel.js';
import { APP_LOCALE } from '../../utils/locale.js';

function statusColor(
  status: InboundPayment['status'],
): 'default' | 'info' | 'warning' | 'success' | 'error' {
  switch (status) {
    case 'PENDING_REVIEW':
      return 'warning';
    case 'UNATTRIBUTED':
      return 'error';
    case 'APPROVING':
    case 'CORRECTING_FEE':
    case 'REVERSING':
      return 'info';
    case 'APPROVED':
      return 'success';
    default:
      return 'default';
  }
}

function DetailValue({
  label,
  value,
  mono = false,
  fullWidth = false,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  fullWidth?: boolean;
}): JSX.Element {
  const shown = value === null || value === undefined || value.trim() === '' ? '—' : value;
  return (
    <Box component="div" sx={{ minWidth: 0, gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <Typography
        component="dt"
        variant="caption"
        sx={{ color: 'text.muted', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          m: 0,
          color: shown === '—' ? 'text.muted' : 'text.primary',
          fontFamily: mono ? 'var(--font-mono)' : undefined,
          overflowWrap: 'anywhere',
        }}
      >
        {shown}
      </Typography>
    </Box>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Box component="section">
      <Typography variant="overline" sx={{ color: 'primary.light', display: 'block', mb: 1.5 }}>
        {title}
      </Typography>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.25,
          m: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Where this money comes from, and whether that is working.
 *
 * An empty queue and a broken connection look exactly the same on this screen.
 * That is not a theoretical complaint: a customer's EUR 10 sat at the partner
 * for a day while this page showed nothing, and there was no way to tell
 * "nobody has paid anybody" from "we have not been able to ask since Tuesday"
 * without shell access to a production box.
 *
 * So the answer is here, next to the empty table, in the words an operator can
 * act on rather than a state name.
 */
export function SourceStatus(): JSX.Element | null {
  const source = useQuery({
    queryKey: ['inbound-source'],
    queryFn: () => inboundPaymentsApi.source(),
    refetchInterval: 30_000,
  });

  const data = source.data;
  // A status panel with an unreadable answer shows nothing; it must never
  // take the transactions page down with it.
  if (data === null || data === undefined || typeof data.summary?.outcomes !== 'object' || data.summary.outcomes === null) return null;

  const { summary } = data;
  const asked = new Date(data.at).toLocaleTimeString(APP_LOCALE);
  const filed = (summary.outcomes['FILED'] ?? 0) + (summary.outcomes['UNATTRIBUTED'] ?? 0);
  const notOurs = summary.outcomes['NOT_OURS'] ?? 0;
  const recorded = summary.outcomes['DUPLICATE'] ?? 0;
  const nonCredits = summary.outcomes['NOT_CREDIT'] ?? 0;

  if (summary.state === 'DISABLED') {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>{summary.provider} is not switched on.</strong> Nothing is being read from them, so
        money arriving at a customer&apos;s account there will not appear here. Switch the
        connection on in Settings → Integrations.
      </Alert>
    );
  }

  if (summary.state === 'UNUSABLE') {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <strong>{summary.provider} is switched on but cannot be read</strong>
        {summary.reason === undefined ? '' : ` — ${summary.reason}`}. Money may be arriving that
        nobody here can see. Checked at {asked}.
      </Alert>
    );
  }

  if (summary.transactionsRead === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Checked {summary.provider} at {asked} — they reported no transactions in the last 14 days on
        the {summary.accountsRead === 0 ? 'account' : 'accounts'} we read. If you can see a payment
        in their console that is not here, their transaction list does not cover the account it
        arrived at.
      </Alert>
    );
  }

  if (filed === 0) {
    return (
      <Alert severity={summary.unreadable > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
        Checked {summary.provider} at {asked} — read {summary.transactionsRead} transactions and
        found no new customer credits. {recorded} already recorded; {nonCredits} debit, fee or
        unclassified records retained as evidence. {notOurs > 0 ? `${notOurs} did not match a customer account and require reconciliation.` : ''}
        {summary.unreadable > 0 ? ` ${summary.unreadable} records could not be read.` : ''}
      </Alert>
    );
  }

  return (
    <Alert severity="success" sx={{ mb: 2 }}>
      Checked {summary.provider} at {asked} — read {summary.transactionsRead} transactions.
      {summary.unreadable > 0 ? ` ${summary.unreadable} could not be read as payments.` : ''}
    </Alert>
  );
}

export function InboundReviewPage({
  reviewId,
  onReviewClose,
  reviewAccounts,
  accountsUnavailable,
}: {
  reviewId?: string;
  onReviewClose?: () => void;
  reviewAccounts?: { senderAccount: string; recipientAccount: string } | undefined;
  accountsUnavailable?: boolean;
} = {}): JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  // DEV-67: releasing money is AML / MLRO work; the commission on it is the
  // client manager's. Everyone else reads the queue.
  const canDecide = can('payments.review');

  const [filter, setFilter] = useState<InboundQueueFilter>(() =>
    queueFilterFromUrl(searchParams.get('status')),
  );
  const [selected, setSelected] = useState<InboundPayment | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');
  const [screeningReady, setScreeningReady] = useState(false);
  const [customer, setCustomer] = useState<CustomerSearchResult | null>(null);
  const [accountId, setAccountId] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(
    () => reviewId ?? searchParams.get('payment'),
  );
  const [recording, setRecording] = useState(false);

  const queue = useQuery({
    queryKey: ['inbound-payments', filter],
    queryFn: () => inboundPaymentsApi.list(filter),
    enabled: !reviewId,
    // Money arrives while the screen is open; a stale queue means a customer
    // waiting on a payment nobody can see.
    refetchInterval: 30_000,
  });

  const customerOptions = useQuery({
    queryKey: ['inbound-attribute-customers', customerQuery],
    queryFn: () => customersApi.search(customerQuery, 20),
    enabled: action === 'attribute' && customerQuery.trim().length >= 2,
  });

  const chosenCustomer = useQuery({
    queryKey: ['inbound-attribute-accounts', customer?.customerId ?? ''],
    queryFn: () => customersApi.getCustomer(customer?.customerId ?? ''),
    enabled: action === 'attribute' && customer !== null,
  });

  const detail = useQuery({
    queryKey: ['inbound-payment-detail', detailId ?? ''],
    queryFn: () => inboundPaymentsApi.get(detailId ?? ''),
    enabled: detailId !== null,
  });

  const close = (): void => {
    setScreeningReady(false);
    setAction(null);
    setSelected(null);
    setReason('');
    setCustomer(null);
    setAccountId('');
    setCustomerQuery('');
    setFailure(null);
    onReviewClose?.();
  };

  const settled = (message: string): void => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['inbound-payment-detail'] });
    setOutcome(message);
    void queryClient.invalidateQueries({ queryKey: ['inbound-payments'] });
    close();
  };

  const failed = (err: unknown): void => {
    setFailure(err instanceof Error ? err.message : 'The request did not go through.');
  };

  const approve = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      inboundPaymentsApi.approve(input.id, input.reason),
    onSuccess: (result) => {
      settled(
        `Released. Credited ${money(result.creditedCents, selected?.currency ?? '')}, charged ${money(
          result.feeCents,
          selected?.currency ?? '',
        )} — the customer receives ${money(result.netCents, selected?.currency ?? '')}.`,
      );
    },
    onError: failed,
  });

  const reject = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      inboundPaymentsApi.reject(input.id, input.reason),
    onSuccess: () => {
      settled('Refused. Nothing was credited — the money is still at the partner.');
    },
    onError: failed,
  });

  const attribute = useMutation({
    mutationFn: (input: { id: string; accountId: string; customerId: string; reason: string }) =>
      inboundPaymentsApi.attribute(input.id, {
        accountId: input.accountId,
        customerId: input.customerId,
        reason: input.reason,
      }),
    onSuccess: () => {
      settled('Attributed. It now needs releasing before the customer is credited.');
    },
    onError: failed,
  });

  /**
   * Take back money already credited. The only action here that lowers a
   * customer's balance, so its confirmation says so in those words.
   */
  /**
   * The running commentary on this payment.
   *
   * A decision already captures a required reason at the moment it is taken.
   * This is different: it can be written before a decision, which is when the
   * thinking happens, and it is what a review months later actually reads.
   */
  const reverse = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      inboundPaymentsApi.reverse(input.id, input.reason),
    onSuccess: (result) => {
      settled(
        result.balanceWarning
          ? 'Reversed. The account is now negative — the customer owes the difference.'
          : 'Reversed. The credit and its fee have been taken back.',
      );
    },
    onError: failed,
  });

  const busy =
    approve.isPending ||
    reject.isPending ||
    attribute.isPending ||
    reverse.isPending;
  const reasonTooShort = reason.trim().length < 4;

  const submit = (): void => {
    if (selected === null) return;
    setFailure(null);
    if (action === 'approve') {
      approve.mutate({ id: selected.id, reason: reason.trim() });
    } else if (action === 'reject') {
      reject.mutate({ id: selected.id, reason: reason.trim() });
    } else if (action === 'reverse') {
      reverse.mutate({ id: selected.id, reason: reason.trim() });
    } else if (action === 'attribute' && customer !== null && accountId !== '') {
      attribute.mutate({
        id: selected.id,
        accountId,
        customerId: customer.customerId,
        reason: reason.trim(),
      });
    }
  };

  const open = (payment: InboundPayment, kind: ActionKind): void => {
    setSelected(payment);
    setAction(kind);
    setReason('');
    setFailure(null);
  };

  const openFromDetail = (payment: InboundPaymentDetail, kind: ActionKind): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    if (!reviewId) setSearchParams(next, { replace: true });
    setDetailId(null);
    open(payment, kind);
  };

  const showDetail = (id: string): void => {
    setDetailId(id);
    setSearchParams({ status: filter, payment: id }, { replace: true });
  };

  const closeDetail = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    if (!reviewId) setSearchParams(next, { replace: true });
    setDetailId(null);
    onReviewClose?.();
  };

  const rows = queue.data ?? [];

  return (
    <Box>
      {!reviewId && (
        <>
          <SourceStatus />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5">Incoming payments</Typography>
              <Typography variant="body2" color="text.secondary">
                Money that has arrived and has not been credited. The customer sees it as pending
                until it is released here.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              {canDecide && (
                <Button variant="outlined" onClick={() => setRecording(true)}>
                  Record a payment
                </Button>
              )}
              <TextField
                select
                size="small"
                label="Show"
                value={filter}
                onChange={(e) => {
                  const nextFilter = e.target.value as InboundQueueFilter;
                  setFilter(nextFilter);
                  setDetailId(null);
                  setSearchParams({ status: nextFilter }, { replace: true });
                }}
                sx={{ minWidth: 240 }}
              >
                {FILTERS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          <RecordManualPaymentDialog
            open={recording}
            onClose={() => setRecording(false)}
            onRecorded={(message) => {
              setRecording(false);
              setOutcome(message);
              void queryClient.invalidateQueries({ queryKey: ['inbound-payments'] });
            }}
          />

          {outcome !== null && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOutcome(null)}>
              {outcome}
            </Alert>
          )}
          {queue.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              The queue could not be read. Nothing has been credited.
            </Alert>
          )}
          {!canDecide && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can see what is waiting. Releasing money is an AML / MLRO action.
            </Alert>
          )}

          <Paper>
            {queue.isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Nothing is waiting.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Received</TableCell>
                    {/* Both parties, side by side. The queue used to name only the
                    payer, so an officer released money having read who sent it
                    and taken who was being paid on trust — and on a shared
                    account, where several customers are paid into one IBAN and
                    told apart by a reference alone, that is the entire
                    decision. */}
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Arrived</TableCell>
                    <TableCell align="right">Fee</TableCell>
                    <TableCell align="right">Customer receives</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Decision</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{new Date(p.createdAt).toLocaleString(APP_LOCALE)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.payerName ?? '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.payerAccount ?? ''}
                        </Typography>
                      </TableCell>
                      {/* Who is being paid, and into which account.
                      This column used to be an email address and nothing else,
                      so an officer releasing money read the payer's name on one
                      side and a login on the other — and the account the money
                      would land in did not appear at all. A customer can hold
                      several; releasing into the wrong one is as wrong as
                      releasing to the wrong customer. */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        {p.customerId !== null && p.customerId !== '' ? (
                          <>
                            <Typography
                              component={RouterLink}
                              to={`/customers/${p.customerId}`}
                              variant="body2"
                              sx={{ color: 'primary.main', display: 'block' }}
                            >
                              {p.recipientName ?? p.recipientEmail ?? 'Unnamed customer'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', fontFamily: 'monospace' }}
                            >
                              {p.beneficiaryAccount === null
                                ? 'account not stated'
                                : p.beneficiaryAccount}
                            </Typography>
                            {/* The name is the identity; the email stays as the way
                            to reach them, not as the answer to "who". */}
                            {p.recipientName !== null && p.recipientEmail !== null ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {p.recipientEmail}
                              </Typography>
                            ) : null}
                          </>
                        ) : (
                          // Not a blank: nobody to credit is the state an officer
                          // has to act on, and it is why the row is in this queue.
                          <Chip size="small" color="error" variant="outlined" label="Nobody yet" />
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240, wordBreak: 'break-word' }}>
                        {p.paymentDetails ?? '—'}
                      </TableCell>
                      <TableCell align="right">{money(p.amountCents, p.currency)}</TableCell>
                      <TableCell align="right">{money(p.feeCents, p.currency)}</TableCell>
                      <TableCell align="right">
                        <strong>{money(p.netCents, p.currency)}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={p.status} color={statusColor(p.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View payment details">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`View details for payment ${p.operationId}`}
                              onClick={() => showDetail(p.id)}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {p.status === 'UNATTRIBUTED' && (
                            <Button
                              size="small"
                              disabled={!canDecide}
                              onClick={() => open(p, 'attribute')}
                            >
                              Attribute
                            </Button>
                          )}
                          {p.screenedAt === null ? (
                            <Tooltip title="This payment has not been screened against the monitoring rules.">
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label="Unscreened"
                              />
                            </Tooltip>
                          ) : p.screeningFlagged.length > 0 ? (
                            <Tooltip
                              title={`Monitoring flagged: ${p.screeningFlagged
                                .map((f) => f.rule ?? 'rule')
                                .join(', ')}`}
                            >
                              <Chip
                                size="small"
                                color="warning"
                                label={`${String(p.screeningFlagged.length)} flagged`}
                              />
                            </Tooltip>
                          ) : null}
                          {p.status === 'PENDING_REVIEW' &&
                            (p.providerRefused ? (
                              // Not a disabled button: a disabled Release reads as
                              // "you lack the rights". The money is not here, and
                              // saying so is the whole point.
                              <Tooltip
                                title={`${p.provider} reports this as "${p.providerStatus ?? 'refused'}". It was not delivered, so there is nothing to release.`}
                              >
                                <Chip size="small" color="error" label="Not delivered" />
                              </Tooltip>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!canDecide}
                                onClick={() => open(p, 'approve')}
                              >
                                Release
                              </Button>
                            ))}
                          {p.status === 'APPROVED' && !p.providerRefused && (
                            <Button
                              size="small"
                              disabled={!canDecide}
                              onClick={() => showDetail(p.id)}
                            >
                              Correct fee
                            </Button>
                          )}
                          {p.status === 'APPROVED' && p.providerRefused && (
                            // Money is already with the customer and the partner
                            // now says it never sent it. This is the only way back.
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              disabled={!canDecide}
                              onClick={() => open(p, 'reverse')}
                            >
                              Take the money back
                            </Button>
                          )}
                          {(p.status === 'PENDING_REVIEW' || p.status === 'UNATTRIBUTED') && (
                            <Button
                              size="small"
                              color="error"
                              disabled={!canDecide}
                              onClick={() => open(p, 'reject')}
                            >
                              Refuse
                            </Button>
                          )}
                          {p.reviewReason !== null && p.reviewedBy !== null && (
                            <Typography variant="caption" color="text.secondary">
                              {p.reviewedBy}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
      <Drawer
        anchor="right"
        open={detailId !== null}
        onClose={closeDetail}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: (t) => t.zIndex.appBar + 1 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 640 },
            maxWidth: '100vw',
            bgcolor: 'background.default',
            backgroundImage:
              'radial-gradient(700px 420px at 100% 0%, rgba(0, 201, 252, 0.08), transparent 60%)',
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(3, 7, 15, 0.88)',
            backdropFilter: 'blur(12px)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: 'primary.light' }}>
                Incoming payment
              </Typography>
              <Typography variant="h5">Payment details</Typography>
              {detail.data !== undefined && (
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1.25 }}>
                  <Chip
                    size="small"
                    label={detail.data.status}
                    color={statusColor(detail.data.status)}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {detail.data.provider}
                  </Typography>
                </Stack>
              )}
            </Box>
            <IconButton onClick={closeDetail} aria-label="Close payment details">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {detail.isLoading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Reading the complete payment instruction…
            </Typography>
          </Box>
        ) : detail.isError || detail.data === undefined ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">The payment details could not be read.</Alert>
          </Box>
        ) : (
          <>
            <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
              {/* Stated before the amounts, like the refusal notice: the
                  numbers are what make a payment look releasable. */}
              {detail.data.screenedAt === null ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <strong>This payment was not screened.</strong>{' '}
                  {detail.data.screeningDetail !== null && detail.data.screeningDetail !== ''
                    ? detail.data.screeningDetail
                    : 'The monitoring provider gave no answer for it.'}{' '}
                  Not screened is not the same as screened and clear &mdash; screen it before
                  releasing, or record why you did not.
                </Alert>
              ) : detail.data.screeningFlagged.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <strong>
                    Monitoring flagged {detail.data.screeningFlagged.length}{' '}
                    {detail.data.screeningFlagged.length === 1 ? 'rule' : 'rules'}.
                  </strong>
                  <Stack component="ul" sx={{ m: '8px 0 0', pl: 2.5 }} spacing={0.5}>
                    {detail.data.screeningFlagged.map((f, i) => (
                      <li key={f.rule ?? String(i)}>
                        <strong>{f.rule ?? 'Rule'}</strong>
                        {f.reason !== undefined && f.reason !== '' ? ` — ${f.reason}` : ''}
                        {f.score !== undefined
                          ? ` (${String(f.score)}${
                              f.maxScore !== undefined ? ` of ${String(f.maxScore)}` : ''
                            })`
                          : ''}
                      </li>
                    ))}
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Overall {String(detail.data.screeningScore ?? 0)} on the provider&rsquo;s own
                    scale, which is not a percentage. You decide &mdash; this is what they found.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Screened by monitoring, no rule flagged
                  {detail.data.screeningStatus !== null && detail.data.screeningStatus !== ''
                    ? ` (${detail.data.screeningStatus})`
                    : ''}
                  .
                </Alert>
              )}
              {detail.data.providerRefused && (
                // Stated before the numbers, because the numbers are what make
                // this look like a payment worth releasing.
                <Alert severity="error" sx={{ mb: 3 }}>
                  <strong>{detail.data.provider} did not deliver this money.</strong> They report it
                  as &ldquo;{detail.data.providerStatus}&rdquo;. Releasing it would credit the
                  customer out of our own funds against a transfer that does not exist, so it cannot
                  be released. Refuse it, or settle the status with {detail.data.provider} first.
                </Alert>
              )}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 3,
                  background:
                    'linear-gradient(145deg, rgba(15, 30, 54, 0.96), rgba(10, 20, 36, 0.96))',
                  borderColor: 'rgba(0, 201, 252, 0.2)',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" gap={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Arrived
                    </Typography>
                    <Typography variant="h4">
                      {money(detail.data.amountCents, detail.data.currency)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      Customer receives
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.light' }}>
                      {money(detail.data.netCents, detail.data.currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fee {money(detail.data.feeCents, detail.data.currency)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Stack spacing={3} divider={<Divider flexItem />}>
                <DetailSection title="Payment instruction">
                  <DetailValue label="Provider operation ID" value={detail.data.operationId} mono />
                  <DetailValue label="End-to-end ID" value={detail.data.endToEndId} mono />
                  <DetailValue label="Instruction ID" value={detail.data.instructionId} mono />
                  <DetailValue label="Rail / service" value={detail.data.serviceType} />
                  <DetailValue label="Direction" value={detail.data.direction} />
                  <DetailValue label="Provider status" value={detail.data.providerStatus} />
                  <DetailValue label="Value date" value={detail.data.valueDate} />
                  <DetailValue
                    label="Received by Norvix"
                    value={new Date(detail.data.createdAt).toLocaleString(APP_LOCALE)}
                  />
                  <DetailValue label="Purpose code" value={detail.data.purposeCode} />
                  <DetailValue
                    label="Payment reference / remittance"
                    value={detail.data.paymentDetails}
                    fullWidth
                  />
                </DetailSection>

                <DetailSection title="Sender">
                  <DetailValue label="Payer name" value={detail.data.payerName} />
                  <DetailValue label="Payer country" value={detail.data.payerCountry} />
                  <DetailValue
                    label="Payer account"
                    value={
                      reviewAccounts
                        ? reviewAccounts.senderAccount || 'Not recorded'
                        : detail.data.payerAccount
                    }
                    mono
                    fullWidth
                  />
                  {accountsUnavailable && (
                    <Alert severity="error">
                      Full accounts could not be loaded or their access audited. Close and reopen
                      this transaction to retry.
                    </Alert>
                  )}
                  <DetailValue
                    label="Sender address (provider)"
                    value={
                      providerTransactionEvidence(detail.data.providerPayload).senderAddress ||
                      'Not supplied by provider'
                    }
                    fullWidth
                  />
                  <DetailValue label="Sender bank" value={detail.data.senderBankName} />
                  <DetailValue label="Sender bank BIC" value={detail.data.senderBankBic} mono />
                  <DetailValue label="Sender bank country" value={detail.data.senderBankCountry} />
                </DetailSection>

                {/* Correct the sender and screen them, or their UBO (DEV-96). Optional. */}
                <SenderScreeningPanel payment={detail.data} canAct={canDecide} />

                <DetailSection title="Beneficiary and match">
                  <DetailValue
                    label="Beneficiary account at provider"
                    value={
                      reviewAccounts
                        ? reviewAccounts.recipientAccount || 'Not recorded'
                        : detail.data.beneficiaryAccount
                    }
                    mono
                    fullWidth
                  />
                  {/* Which of our accounts at the partner received it. Shared
                      accounts take money for many customers, so "who was paid"
                      and "where it landed" are different questions. */}
                  <DetailValue
                    label="Verification of Payee (provider)"
                    value={
                      providerTransactionEvidence(detail.data.providerPayload).payeeVerification ||
                      'No provider result recorded'
                    }
                    fullWidth
                  />
                  <DetailValue label="At partner" value={detail.data.provider} />
                  <DetailValue label="Norvix customer ID" value={detail.data.customerId} mono />
                  <DetailValue label="Norvix account ID" value={detail.data.accountId} mono />
                  {detail.data.customerId !== null && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Button
                        component={RouterLink}
                        to={`/customers/${detail.data.customerId}`}
                        size="small"
                        variant="outlined"
                      >
                        Open customer profile
                      </Button>
                    </Box>
                  )}
                </DetailSection>

                {can('payments.workspace') && <IncomingCommissionPanel key={detail.data.id} paymentId={detail.data.id} canEdit={can('fees.edit')} />}

                <DetailSection title="Processing record">
                  <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                    <ReviewDocumentsPanel key={detail.data.id} kind="incoming" id={detail.data.id} canWrite={canDecide} />
                  </Box>
                  {/* One decision, one entry (DEV-95): a decision on the case
                      file appears in the comments below with its author, time
                      and reason. Only an older decision, from before that, is
                      shown in these fields, so nothing already recorded is lost. */}
                  {detail.data.decisionOnFile !== true && detail.data.reviewedAt !== null ? (
                    <>
                      <DetailValue label="Reviewed by" value={detail.data.reviewedBy} mono />
                      <DetailValue label="Reviewed at" value={new Date(detail.data.reviewedAt).toLocaleString(APP_LOCALE)} />
                      <DetailValue label="Recorded reason" value={detail.data.reviewReason} fullWidth />
                    </>
                  ) : null}
                  {/* The same comment mechanic as everywhere else. It used to
                    be its own box that could not be edited once saved, which is
                    how a correction became a second note contradicting the
                    first. */}
                  <Box sx={{ mt: 2, gridColumn: '1 / -1', minWidth: 0 }}>
                    <CommentsPanel
                      subjectType="INBOUND_PAYMENT"
                      subjectId={detail.data.id}
                      customerId={detail.data.customerId ?? ''}
                      canWrite={can('comments.write') && detail.data.customerId !== null}
                      title="Comments"
                      dense
                    />
                  </Box>
                </DetailSection>
              </Stack>

              <Accordion
                disableGutters
                sx={{ mt: 3, bgcolor: 'background.paper', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="subtitle2">{detail.data.providerPayloadSource?.startsWith('RECOVERED_') ? 'Recovered provider payload' : 'Original provider payload'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detail.data.providerPayloadSource?.startsWith('RECOVERED_')
                        ? 'Retrieved from the provider after import; the original import response was not saved.'
                        : 'The provider response retained as received.'}
                      {detail.data.providerPayloadAt ? ` Recorded ${new Date(detail.data.providerPayloadAt).toLocaleString(APP_LOCALE)}.` : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      maxHeight: 320,
                      overflow: 'auto',
                      borderRadius: 1,
                      bgcolor: 'rgba(3, 7, 15, 0.7)',
                      color: 'text.secondary',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {detail.data.providerPayload === null
                      ? 'No original provider payload was retained for this payment.'
                      : JSON.stringify(detail.data.providerPayload, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            {detail.data.status === 'APPROVED' && detail.data.providerRefused && (
              <Stack direction="row" justifyContent="flex-end" sx={{ p: 3 }}>
                <Button
                  disabled={!canDecide}
                  variant="outlined"
                  color={detail.data.providerRefused ? 'error' : 'primary'}
                  onClick={() =>
                    openFromDetail(
                      detail.data,
                      'reverse',
                    )
                  }
                >
                  Take the money back
                </Button>
              </Stack>
            )}
            {(detail.data.status === 'PENDING_REVIEW' || detail.data.status === 'UNATTRIBUTED') && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{
                  mt: 'auto',
                  px: { xs: 2.5, sm: 3 },
                  py: 2,
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: 'rgba(3, 7, 15, 0.94)',
                  backdropFilter: 'blur(12px)',
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                {detail.data.status === 'UNATTRIBUTED' && (
                  <Button
                    variant="outlined"
                    disabled={!canDecide}
                    onClick={() => openFromDetail(detail.data, 'attribute')}
                  >
                    Attribute
                  </Button>
                )}
                <Button
                  color="error"
                  disabled={!canDecide}
                  onClick={() => openFromDetail(detail.data, 'reject')}
                >
                  Refuse
                </Button>
                {detail.data.releaseBlockedReason && <Alert severity="error">{detail.data.releaseBlockedReason}</Alert>}
                {detail.data.status === 'PENDING_REVIEW' && !detail.data.providerRefused && !detail.data.releaseBlockedReason && (
                  <Button
                    variant="contained"
                    disabled={!canDecide}
                    onClick={() => openFromDetail(detail.data, 'approve')}
                  >
                    Release
                  </Button>
                )}
              </Stack>
            )}
          </>
        )}
      </Drawer>

      <Dialog open={action !== null} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
        <DialogTitle>
          {action === 'approve'
            ? 'Release this payment'
            : action === 'reject'
              ? 'Refuse this payment'
              : action === 'reverse'
                ? 'Take this credit back'
                : 'Attribute this payment'}
        </DialogTitle>
        <DialogContent>
          {selected !== null && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {selected.payerName ?? 'Unnamed payer'} —{' '}
                  {selected.paymentDetails ?? 'no reference'}
                </Typography>
                <Typography variant="h6">
                  {money(selected.amountCents, selected.currency)} arrived
                </Typography>
                <Typography variant="body2">
                  Fee {money(selected.feeCents, selected.currency)} — the customer receives{' '}
                  <strong>{money(selected.netCents, selected.currency)}</strong>
                </Typography>
                {action === 'approve' && (
                  <Typography variant="caption" color="text.secondary">
                    A staff-edited commission is retained. Otherwise, the current tariff is confirmed when you release the payment.
                  </Typography>
                )}
                {action === 'reverse' && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    The customer&rsquo;s balance goes <strong>down</strong> by the full amount and
                    the fee is returned. If they have already spent it the account goes negative,
                    which is a debt to collect — the reversal still happens, because leaving it
                    would mean our books claim money that does not exist.
                  </Alert>
                )}
              </Box>

              {action === 'attribute' && (
                <>
                  <TextField
                    label="Find the customer"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    helperText="Name, email or phone — at least two characters."
                  />
                  <TextField
                    select
                    label="Customer"
                    value={customer?.customerId ?? ''}
                    onChange={(e) => {
                      const found = (customerOptions.data ?? []).find(
                        (c) => c.customerId === e.target.value,
                      );
                      setCustomer(found ?? null);
                      setAccountId('');
                    }}
                    disabled={customerQuery.trim().length < 2}
                    helperText={
                      customerOptions.isFetching
                        ? 'Searching…'
                        : (customerOptions.data ?? []).length === 0 &&
                            customerQuery.trim().length >= 2
                          ? 'Nobody matched that.'
                          : 'Whose money this is.'
                    }
                  >
                    {(customerOptions.data ?? []).map((c) => (
                      <MenuItem key={c.customerId} value={c.customerId}>
                        {c.maskedName} — {c.maskedEmail}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Account to credit"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    disabled={customer === null || chosenCustomer.isLoading}
                    helperText={
                      customer === null
                        ? 'Pick the customer first.'
                        : 'Only an account in the payment’s currency can take it.'
                    }
                  >
                    {(chosenCustomer.data?.accounts ?? [])
                      .filter((a) => a.currency === selected.currency)
                      .map((a) => (
                        <MenuItem key={a.accountId} value={a.accountId}>
                          {a.iban} — {a.currency}
                        </MenuItem>
                      ))}
                  </TextField>
                </>
              )}

              <TextField
                label="Reason"
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={reason.trim().length > 0 && reasonTooShort}
                helperText={
                  reasonTooShort
                    ? 'At least 4 characters — this is the record of why the money moved.'
                    : 'Kept against the payment.'
                }
              />

              {action === 'approve' && (
                <PaymentScreeningPanel
                  key={`screening-${selected.id}`}
                  path={`/admin/inbound-payments/${selected.id}`}
                  onReady={setScreeningReady}
                />
              )}

              {failure !== null && <Alert severity="error">{failure}</Alert>}
              <ReviewDocumentsPanel key={`documents-${selected.id}`} kind="incoming" id={selected.id} canWrite={canDecide} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'reject' ? 'error' : 'primary'}
            onClick={submit}
            disabled={
              busy ||
              reasonTooShort ||
              (action === 'approve' && !screeningReady) ||
              (action === 'attribute' && (customer === null || accountId === ''))
            }
          >
            {action === 'approve'
              ? 'Release the money'
              : action === 'reject'
                ? 'Refuse'
                : action === 'reverse'
                  ? 'Take the money back'
                  : 'Attribute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
